import { useParams, Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import apiService from '../services/api'
import { ArrowLeft, User, Mail, Phone, Building, MapPin, Calendar, Euro, CheckCircle, Clock, XCircle, Package } from 'lucide-react'

const AdminReservationDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const initial = location.state?.reservation
  const { data, isLoading } = useQuery(
    ['reservation', id],
    async () => {
      try {
        const res = await apiService.getReservation(id)
        return res.data
      } catch (e) {
        if (e?.response?.status === 404 && initial) {
          return { reservation: initial }
        }
        return { reservation: initial }
      }
    },
    {
      enabled: !!id,
      initialData: initial ? { reservation: initial } : undefined,
      retry: false,
      refetchOnWindowFocus: false
    }
  )

  const r = data?.reservation || initial

  const formatCurrency = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0))
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : ''
  const statusMeta = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'confirmed':
        return { color: 'text-green-700 bg-green-100', icon: <CheckCircle className="h-4 w-4" /> }
      case 'demande_envoyee':
        return { color: 'text-orange-700 bg-orange-100', icon: <Clock className="h-4 w-4" /> }
      case 'cancelled':
        return { color: 'text-red-700 bg-red-100', icon: <XCircle className="h-4 w-4" /> }
      default:
        return { color: 'text-gray-600 bg-gray-100', icon: <Clock className="h-4 w-4" /> }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Chargement…</div>
      </div>
    )
  }

  if (!r) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Réservation introuvable</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate('/admin')} className="btn-outline inline-flex items-center">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </button>
            <h1 className="text-2xl font-bold">Informations</h1>
          </div>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusMeta(r.status).color}`}>
            {statusMeta(r.status).icon}
            <span className="ml-1 capitalize">{r.status || 'inconnu'}</span>
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="card lg:col-span-2">
            <h2 className="text-lg font-semibold mb-2">Votre réservation N°: {r.id}</h2>
            <div className="text-sm text-gray-700 mb-4 flex items-center"><Calendar className="h-4 w-4 mr-2 text-gray-500" /> Période de location: {formatDate(r.start_date)} - {formatDate(r.end_date)}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h3 className="font-semibold mb-2 flex items-center"><User className="h-4 w-4 mr-2 text-orange-500" /> Client</h3>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <div className="flex items-center"><Mail className="h-4 w-4 mr-2 text-gray-500" /> {r.first_name} {r.last_name} ({r.email})</div>
                    {r.phone && (<div className="flex items-center"><Phone className="h-4 w-4 mr-2 text-gray-500" /> {r.phone}</div>)}
                    {r.company_name && (<div className="flex items-center"><Building className="h-4 w-4 mr-2 text-gray-500" /> {r.company_name}</div>)}
                    {(r.street || r.postal_code || r.city) && (
                      <div className="flex items-start"><MapPin className="h-4 w-4 mr-2 text-gray-500" />
                        <span>{[r.street, r.street_number].filter(Boolean).join(' ')}<br />{[r.postal_code, r.city].filter(Boolean).join(' ')}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 flex items-center text-gray-800"><MapPin className="h-4 w-4 mr-2 text-orange-500" /> Adresse de facturation</h4>
                    {(r.invoice_street || r.invoice_street_number || r.invoice_postal_code || r.invoice_city) ? (
                      <div className="text-sm text-gray-700">
                        <div>{[r.invoice_street, r.invoice_street_number].filter(Boolean).join(' ')}</div>
                        <div>{[r.invoice_postal_code, r.invoice_city].filter(Boolean).join(' ')}</div>
                        {r.invoice_state && (<div>{r.invoice_state}</div>)}
                        {r.invoice_country && (<div>{r.invoice_country.toUpperCase()}</div>)}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Non renseignée</div>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2 flex items-center"><Package className="h-4 w-4 mr-2 text-orange-500" /> Équipement</h3>
                <div className="space-y-3">
                  {r.products && Array.isArray(r.products) && r.products.length > 0 ? (
                    <div className="space-y-2">
                      {r.products.map((product, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-900">{product.name || 'Produit'}</span>
                            <span className="text-sm text-gray-600">Qté: {product.quantity || 1}</span>
                          </div>
                          {product.price_per_day && (
                            <div className="text-sm text-gray-600">
                              Prix/jour: {formatCurrency(product.price_per_day)}
                            </div>
                          )}
                          {product.total_price && (
                            <div className="text-sm font-medium text-orange-600">
                              Sous-total: {formatCurrency(product.total_price)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-gray-900">{r.produit_name || 'Produit'}</div>
                    </div>
                  )}
                  <div className="flex items-center pt-2 border-t border-gray-200">
                    <Euro className="h-4 w-4 mr-2 text-gray-500" />
                    <span className="text-gray-600">Total:</span>
                    <span className="ml-1 font-semibold text-lg">{formatCurrency(r.total_price)}</span>
                  </div>
                </div>
              </div>
            </div>
            {r.notes && (
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Notes</h3>
                <div className="p-4 rounded bg-gray-50 border border-gray-200 text-sm text-gray-700">{r.notes}</div>
              </div>
            )}
          </div>
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Actions</h2>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="text-gray-500">Bientôt: changer le statut, renvoyer l'email, exporter Rentman…</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminReservationDetail


